import { mountVNode, type Recording, sanitizeObject, transcriptionToVTT } from 'core'
import { useAudioPlayer } from './audio-player'
import { getData, useRecordingState } from './data'

export namespace Transcription {
  /** Assigns index as id to transcription sentences */
  export function fromRawContent(source: Recording.ITranscription) {
    const input = Array.from(source)

    let index = -1

    return input.map(i =>
      ({
        ...i,
        sentences: i.sentences.map((item) => {
          return {
            ...item,
            id: ++index
          }
        })
      })
    )
  }

  export type IContent = ReturnType<typeof fromRawContent>
  export type IContentParagraph = IContent[0]
  export type IContentSentence = IContentParagraph['sentences'][0]
  export type IStatus = Record<Recording['transcriptionStatus'], boolean>
}

function useCaptionsLoader(content: Ref<Transcription.IContent>) {
  const audioRef = useAudioPlayer()
  const activeTrack = ref<number>()

  const trackID = useId()
  const track = h('track', {
    id: trackID,
    srclang: 'en',
    default: true,
    kind: 'captions',
    onCuechange: onCueChange,
    onLoad: onTrackLoaded,
    onVnodeBeforeUnmount(vnode) {
      URL.revokeObjectURL(vnode.props!.src)
    }
  })

  const isValid = () => content.value.length > 0

  function getTextTrack() {
    const track = audioRef.audio.textTracks.getTrackById(trackID)

    if (!track)
      throw new Error(`No text track found with id - ${trackID}`)

    const cues = Array.from(track.cues || [])
    const getCue = (id: number | string) => track.cues?.getCueById(String(id))

    return { track, cues, getCue }
  }

  function onTrackLoaded() {
    const { cues } = getTextTrack()

    // Assign index as id to cues
    cues.forEach((cue, index) => cue.id = String(index))
    console.debug('Captions track has loaded', { cues })
  }

  function onCueChange() {
    const { track } = getTextTrack()
    const cue = track.activeCues?.[0]

    if (cue)
      activeTrack.value = +cue.id
  }

  function load() {
    const file = new Blob(
      [transcriptionToVTT(content.value)],
      { type: 'text/vtt' }
    )

    // Update track src
    track.props ||= {}
    track.props.src = URL.createObjectURL(file)

    mountVNode(track, { element: audioRef.audio })
    console.debug('Captions file mounted')
  }

  onMounted(() => {
    if (isValid())
      return load()

    // Wait for transcription to load
    watchOnce(() => isValid(), load)
  })

  return { activeTrack }
}

function useCaptionsEditor(audioId: string, transcription: Ref<Transcription.IContent>) {
  const canEdit = import.meta.client ? !!useAudioFolders().getItem(audioId) : false
  const isEnabled = ref(false)

  const updateServer = debounce(500, () => {
    const content = sanitizeObject(transcription.value).map(i => ({
      ...i,
      sentences: i.sentences.map((j) => {
        const { id, ...rest } = j
        return rest
      })
    }))

    http.post(`/update-transcription/${audioId}`, content)
    track('Updated transcription', { audioId, content })
  })

  function update(cueId: number, text: string) {
    // Update cue text in transcription
    for (const item of transcription.value) {
      for (const sentence of item.sentences) {
        if (sentence.id === cueId) {
          sentence.text = text
          break
        }
      }
    }

    // Trigger update
    updateServer()
  }

  /** Toggles captions editor */
  function toggle() {
    isEnabled.value = !isEnabled.value
  }

  return toReactive({ canEdit, isEnabled, update, toggle })
}

export function useCaptions() {
  const source = useRecordingState()
  const state = reactiveComputed(() => {
    const data = source.data.value
    const rawTranscription = data?.audio.transcription || []
    const transcription = Transcription.fromRawContent(rawTranscription)

    return {
      transcription,

      /** Whether to show transcriptions */
      isEnabled: !!data?.settings.transcriptions && transcription.length > 0,

      /** Whether to show transcriptions */
      isParagraphed: transcription.some(i => i.sentences.length > 1),

      /** Status of transcription */
      status: { [data?.audio.transcriptionStatus || 'loading']: true } as Transcription.IStatus
    }
  })

  const stateRefs = toRefs(state)
  const utils = useCaptionsLoader(stateRefs.transcription)
  const editor = useCaptionsEditor(source.data.value!.audio.id, stateRefs.transcription)

  /** Loads transcription data on client side */
  const refreshTranscription = async () => {
    if (!state.status.loading)
      return

    console.debug('Refreshing transcription status')
    await source.refresh()
    setTimeout(refreshTranscription, 5000)
  }

  onBeforeMount(() => {
    refreshTranscription()
  })

  return { ...utils, ...stateRefs, editor }
}

export function useSummary() {
  const data = getData()
  const value = data.audio.summary

  const isValid = !!value && data.audio.duration >= 120
  const isActive = ref(false)

  return toReactive({ value, isValid, isActive })
}
